import { RouteConfig } from '../types';
import { ONBOARDING_KEYS } from '../constants';
import { HELP_CENTER_LINKS } from '../helpCenterLinks';

export const alertLogPageSteps = [
  {
    element: '[data-onboarding="admin-alerts-dashboard"]',
    popover: {
      title: 'Alerts Dashboard',
      description:
        `View all historical alerts for your organisation on this page. Each alert includes a unique ID, timestamp, and <a href="${HELP_CENTER_LINKS.alertStatus}" target="_blank" class="text-lime underline">status</a>.`,
      side: 'top' as const,
      align: 'center' as const,
    },
  },
  {
    element: 'input[placeholder*="Alert ID"]',
    popover: {
      title: 'Search by Alert ID',
      description:
        'Enter an alert ID to find a specific alert.',
      side: 'bottom' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="admin-alerts-column-toggle"] div[class*="justify-end"] > button:first-of-type',
    popover: {
      title: 'Column Visibility',
      description:
        'Show or hide columns to customise the table.',
      side: 'bottom' as const,
      align: 'end' as const,
    },
  },
  {
    element: '[data-onboarding="admin-alerts-filters-panel"]',
    popover: {
      title: 'Filters Panel',
      description:
        'Apply one or more filters to narrow the alert list. Hide the panel when you\'re finished.',
      side: 'right' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="admin-alerts-dashboard"] + * table tbody, [data-onboarding="admin-alerts-dashboard"] ~ * table tbody',
    popover: {
      title: 'Alerts Table',
      description:
        'All alerts that match your search or filters are listed here. Click on an alert ID to open its full details.',
      side: 'top' as const,
      align: 'start' as const,
    },
  },
];

export const alertLogDetailSteps = [
  {
    element: '[data-onboarding="admin-alert-events"]',
    popover: {
      title: 'Event Viewer',
      description:
        'Browse events linked to this alert. Click an event to see its details and images. This helps you understand what happened from start to finish.',
      side: 'right' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="admin-alert-image-viewer"]',
    popover: {
      title: 'Event Image Viewer',
      description:
        'The centre panel displays a snapshot of the selected event. It shows the timestamp, camera info, and any detection boxes that explain why the alert triggered.',
      side: 'left' as const,
      align: 'center' as const,
    },
  },
  {
    element: '[data-onboarding="admin-alert-navigation"]',
    popover: {
      title: 'Event Navigation',
      description:
        'Move through events using these buttons or your arrow keys. The counter shows your place in the event timeline.',
      side: 'top' as const,
      align: 'center' as const,
    },
  },
  {
    element: '[data-onboarding="admin-alert-details"]',
    popover: {
      title: 'Alert Details',
      description:
        'View key alert information here, such as status, timestamps, and location.',
      side: 'left' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="admin-alert-intruder-details"]',
    popover: {
      title: 'Intruder Details',
      description:
        'If the alert is tagged as an intruder, extra details about detected people or vehicles appear here.',
      side: 'left' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="admin-alert-reports"]',
    popover: {
      title: 'Reports',
      description:
        'Generate and download a report for this alert. Reports include timelines, images, notes, and other information you may need to share.',
      side: 'left' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="admin-alert-notes"]',
    popover: {
      title: 'Notes',
      description:
        'Read the activity log and add notes about the alert. Notes help record actions, observations, and decisions for future reference.',
      side: 'left' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="admin-alert-actions"]',
    popover: {
      title: 'Alert Actions',
      description:
        'Use these options to manage the alert. You can close it, update the status, or disarm it to keep records accurate.',
      side: 'top' as const,
      align: 'start' as const,
    },
  },
];

export const alertLogRoutes: RouteConfig[] = [
  {
    id: 'admin-alerts',
    processName: ONBOARDING_KEYS.adminAlerts,
    match: (path: string) => path === '/alerts',
    steps: alertLogPageSteps,
  },
  {
    id: 'admin-alert-detail',
    processName: ONBOARDING_KEYS.adminAlertDetail,
    match: (path: string) => path.startsWith('/monitoring/alert/'),
    steps: alertLogDetailSteps,
  },
];
